import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { IdentityContextProvider, useIdentity } from "@/lib/identity/useIdentity";
import { POPUPS, matchRoute, type PopupConfig } from "@/lib/identity/popupConfig";
import { canShow, markShown } from "@/lib/identity/frequency";
import { track } from "@/lib/identity/tracker";
import { pageView as ciPageView, markScroll as ciMarkScroll } from "@/lib/customer-intelligence/tracker";
import { SmartPopup } from "./SmartPopup";
import { ArticleReadTracker } from "@/components/blog/ArticleReadTracker";
import { LeadAuthSync } from "./LeadAuthSync";
import { AutoPushOptIn } from "@/components/notifications/AutoPushOptIn";

/**
 * IdentityProvider — mount once at the app shell.
 *
 *  - Generates anonymous id on first paint.
 *  - Tracks page_view + scroll_depth + time_on_page automatically.
 *  - Runs the global popup trigger engine (exit-intent, scroll%, time-on-page,
 *    route, action). To fire an "action" popup from anywhere:
 *
 *      window.dispatchEvent(new CustomEvent("identity:action",
 *        { detail: { action: "add_to_cart" } }));
 *
 *    Map the action name to a popup in popupConfig.ts.
 */
export function IdentityProvider({ children }: { children: React.ReactNode }) {
  return (
    <IdentityContextProvider>
      {children}
      <ArticleReadTracker />
      <LeadAuthSync />
      <PushResync />
      <AutoPushOptIn />

      <IdentityEngine />
    </IdentityContextProvider>
  );
}

/**
 * Repairs subscriber records for browsers that already granted notification
 * permission but have no stored device token. Never prompts.
 */
function PushResync() {
  useEffect(() => {
    const t = window.setTimeout(() => {
      void import("@/lib/push/client").then((m) => m.syncPushSubscriptionIfGranted());
    }, 3000);
    return () => window.clearTimeout(t);
  }, []);
  return null;
}


function IdentityEngine() {
  const { isIdentified, anonymousId } = useIdentity();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [active, setActive] = useState<PopupConfig | null>(null);
  const lastPath = useRef<string>("");
  const triggeredThisSession = useRef<Set<string>>(new Set());

  // page_view + reset per-route state (SPA-aware: rolls page_view_id)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    if (!anonymousId) return;
    // New pipeline: rolls page_view_id + resets per-page scroll max.
    ciPageView(pathname);
    // Legacy lead_events write is still emitted by `track()` below.
    track({ eventType: "page_view", route: pathname, properties: { referrer: document.referrer || null } });
  }, [pathname, anonymousId]);

  // scroll_depth + scroll% triggers
  useEffect(() => {
    if (typeof window === "undefined") return;
    let maxPct = 0;
    let lastReported = 0;
    const milestones = [25, 50, 75, 100];
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - window.innerHeight;
      if (total <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / total) * 100));
      if (pct > maxPct) maxPct = pct;
      ciMarkScroll(maxPct);
      for (const m of milestones) {
        if (maxPct >= m && lastReported < m) {
          lastReported = m;
          track({ eventType: "scroll_depth", properties: { percent: m } });
        }
      }
      tryTriggerScroll(maxPct);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isIdentified]);

  // time on page (sent on leave) + time triggers
  useEffect(() => {
    if (typeof window === "undefined") return;
    const start = Date.now();
    const timers: number[] = [];

    // schedule time-based popup triggers for this route
    for (const p of POPUPS) {
      if (!matchRoute(p.routes, pathname)) continue;
      if (p.trigger.kind !== "time") continue;
      if (p.skipIfIdentified && isIdentified) continue;
      if (triggeredThisSession.current.has(p.id)) continue;
      if (!isDeviceOk(p)) continue;
      const id = window.setTimeout(() => tryOpen(p), p.trigger.seconds * 1000);
      timers.push(id);
    }

    // route-immediate triggers
    for (const p of POPUPS) {
      if (!matchRoute(p.routes, pathname)) continue;
      if (p.trigger.kind !== "route") continue;
      if (p.skipIfIdentified && isIdentified) continue;
      if (!isDeviceOk(p)) continue;
      const id = window.setTimeout(() => tryOpen(p), 800);
      timers.push(id);
    }

    return () => {
      timers.forEach((t) => clearTimeout(t));
      const seconds = Math.round((Date.now() - start) / 1000);
      if (seconds > 2) {
        track({ eventType: "time_on_page", properties: { seconds }, route: pathname });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isIdentified]);

  // exit-intent (desktop only)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const onLeave = (e: MouseEvent) => {
      if (e.clientY > 0) return;
      for (const p of POPUPS) {
        if (p.trigger.kind !== "exit-intent") continue;
        if (!matchRoute(p.routes, pathname)) continue;
        if (p.skipIfIdentified && isIdentified) continue;
        if (!isDeviceOk(p)) continue;
        if (tryOpen(p)) break;
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isIdentified]);

  // action triggers
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { action?: string; properties?: Record<string, unknown> } | undefined;
      const action = detail?.action;
      if (!action) return;
      // track every action as an event too
      track({ eventType: action, properties: detail?.properties ?? {} });
      for (const p of POPUPS) {
        if (p.trigger.kind !== "action") continue;
        if (p.trigger.action !== action) continue;
        if (!matchRoute(p.routes, pathname)) continue;
        if (p.skipIfIdentified && isIdentified) continue;
        if (!isDeviceOk(p)) continue;
        if (tryOpen(p)) break;
      }
    };
    window.addEventListener("identity:action", handler as EventListener);
    return () => window.removeEventListener("identity:action", handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isIdentified]);

  function tryOpen(p: PopupConfig): boolean {
    if (active) return false;
    if (!canShow(p.id)) return false;
    if (triggeredThisSession.current.has(p.id)) return false;
    triggeredThisSession.current.add(p.id);
    markShown(p.id);
    setActive(p);
    track({ eventType: "popup_shown", properties: { popup_id: p.id, brand: p.brand } });
    return true;
  }

  function tryTriggerScroll(currentPct: number) {
    for (const p of POPUPS) {
      if (p.trigger.kind !== "scroll") continue;
      if (currentPct < p.trigger.percent) continue;
      if (!matchRoute(p.routes, pathname)) continue;
      if (p.skipIfIdentified && isIdentified) continue;
      if (!isDeviceOk(p)) continue;
      if (tryOpen(p)) break;
    }
  }

  if (!active) return null;
  return <SmartPopup popup={active} open onClose={() => setActive(null)} />;
}

function isDeviceOk(p: PopupConfig): boolean {
  if (!p.devices || p.devices === "all") return true;
  if (typeof window === "undefined") return false;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  return p.devices === "mobile" ? coarse : !coarse;
}
